import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Link, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import Swal from "sweetalert2";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const DevTicketList = () => {
  const [ticketList, setTicketList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Filters
  const [userNameFilter, setUserNameFilter] = useState("");
  const [queryFilter, setQueryFilter] = useState("");
  const [createdOnDate, setCreatedOnDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;
  const token = localStorage.getItem("authToken");

  // 🔹 Helper to format date as yyyy-MM-dd in local timezone
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fetch symbols from API
  const fetchTicket = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}devTicket/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          limit: limit,
          page: page,
          name: userNameFilter,
          query: queryFilter,
          ticket_status: statusFilter,
          ...(createdOnDate && {
            created_on: formatLocalDate(createdOnDate), // ✅ FIXED: use local date, not UTC
          }),
        },
      });
      const data = response.data;
      setTicketList(data.ticketList);
      setPageCount(data.links.totalPages);
      setTotal(data.totalRecords);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [page, userNameFilter, queryFilter, statusFilter, createdOnDate]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata", // or your timezone
      })
      .replace(/\//g, "-")
      .replace(",", "")
      .toUpperCase();
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${API_URL}devTicket/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        setTicketList((prev) => prev.filter((dt) => dt.id !== id));

        Swal.fire(
          "Deleted!",
          response.data.message || "Your record has been deleted.",
          "success"
        );
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message ||
          "Something went wrong while deleting.",
          "error"
        );
      }
    }
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setUserNameFilter("");
      setQueryFilter("");
      setCreatedOnDate(null);
      setStatusFilter("");
      // setPage(1); // reset pagination
    };

    window.addEventListener(
      "clearDevTicketFiltersEvent",
      handleClearFilters
    );

    return () => {
      window.removeEventListener(
        "clearDevTicketFiltersEvent",
        handleClearFilters
      );
    };
  }, []);


  return (
    <div className="page-header-fixed page-sidebar-fixed fade in mt-5">
      {/* <div id="content">{loading && <LoadingSpinner />}</div> */}
      <div className="panel  panel-default with-rounded-corner with-shadow">
        <div className="d-flex align-items-center justify-content-between">
          <div className="panel-heading border-0">
            <h4 className="panel-title">Manage Ticket</h4>
          </div>
          <div className="d-flex gap-2 me-3">
            <PermissionWrapper module="dev_ticket_listing" action="add">
              <Link
                className="btn btn-default btn-sm pull-right"
                to="/DevTicket/Ticket/add"
              >
                <i className="ti ti-plus"></i> Add Ticket
              </Link>
            </PermissionWrapper>
            <div>
              <span style={{ fontSize: "0.8rem" }} className="me-2">
                {start} to {end} of {total}
              </span>
              <button
                className="btn btn-default btn-sm"
                title="Previous"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className="btn btn-default btn-sm"
                title="Next"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === pageCount}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-hover bg-white">
            <thead className="thead-light">
              <tr>
                <th className="text-center">#</th>
                <th>User</th>
                <th>Query</th>
                <th>Created On</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
              <tr>
                <th></th>
                <th>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="User"
                    value={userNameFilter}
                    onChange={(e) => setUserNameFilter(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Query"
                    value={queryFilter}
                    onChange={(e) => setQueryFilter(e.target.value)}
                  />
                </th>
                <th>
                  <div className="d-flex align-items-center">
                    <DatePicker
                      selected={createdOnDate}
                      onChange={(date) => setCreatedOnDate(date)}
                      isClearable
                      placeholderText="Select created date"
                      dateFormat="yyyy-MM-dd"
                    />
                    <span
                      className="input-group-text"
                      onClick={() => fetchTicket()}
                    >
                      <FontAwesomeIcon icon={faSearch} />
                    </span>
                  </div>
                </th>
                <th>
                  <select
                    className="form-control"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="" selected="selected">
                      Show All
                    </option>
                    <option value="0">Requested</option>
                    <option value="1">Open</option>
                    <option value="2">Closed</option>
                  </select>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, index) => (
                  <tr key={index} className="skeleton-row">
                    <td className="text-center">
                      <div className="skeleton-box" />
                    </td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                  </tr>
                ))
              ) : ticketList.length > 0 ? (
                ticketList.map((post, index) => {
                  let status = (
                    <div className="btn btn-danger btn-xs">Closed</div>
                  );
                  if (post.ticket_status == 0) {
                    status = (
                      <div className="btn btn-info btn-xs">Requested</div>
                    );
                  } else if (post.ticket_status == 1) {
                    status = <div className="btn btn-primary btn-xs">Open</div>;
                  }

                  return (
                    <tr
                      key={post.id}
                      onClick={() => {
                        if (!hasPermission("dev_ticket_listing", "edit")) {
                          return; // no navigation without edit permission
                        }
                        navigate(`/DevTicket/Ticket/edit/${post.id}`, {
                          state: {
                            ticketIds: ticketList.map((t) => t.id),
                          },
                        });
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td
                        style={{ color: "#0d6efd", cursor: "pointer" }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.textDecoration = "underline")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.textDecoration = "none")
                        }
                      >
                        {hasPermission("hrms_user", "edit") ? (
                          <a
                            href={`/Hrms/User/edit/${post.userid}`}
                            onClick={(e) => {
                              e.preventDefault(); // prevent default link navigation
                              e.stopPropagation();
                              navigate(`/Hrms/User/edit/${post.userid}`);
                            }}
                            target="_blank"
                            style={{
                              color: "#0d6efd",
                              textDecoration: "inherit", // inherit underline from td hover
                            }}
                          >
                            {post.user_fullname}
                          </a>
                        ) : (
                          <span style={{ color: "#0d6efd" }}>
                            {post.user_fullname}
                          </span>
                        )}
                      </td>

                      <td>{post.ticket_title}</td>
                      <td className="ctc">{formatDate(post.created_on)}</td>
                      <td>{status}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <PermissionWrapper
                            module="dev_ticket_listing"
                            action="edit"
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/DevTicket/Ticket/edit/${post.id}`, {
                                  state: {
                                    ticketIds: ticketList.map((t) => t.id),
                                  },
                                });
                              }}
                              className="btn btn-warning btn-xs"
                              title="Edit"
                            >
                              <i className="ti ti-pencil"></i>
                            </button>
                          </PermissionWrapper>
                          <PermissionWrapper
                            module="dev_ticket_listing"
                            action="delete"
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(post.id);
                              }}
                              className="cancel1 btn btn-danger btn-xs"
                              title="Delete"
                            >
                              <i className="ti ti-trash"></i>
                            </button>
                          </PermissionWrapper>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    No ticket found matching your criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DevTicketList;
